import { useState } from 'react';
import { onramps, offramps, type OrderFilters } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Empty, Spinner } from '../components/ui';

/**
 * Ramp orders, read-only. Orders are created through the API by the
 * integrator's own system — the dashboard is for observing them.
 */

function orderTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'expired') return 'neutral';
  return 'warning';
}

// A ramp channel that is never funded expires — most accounts have far more
// expired rows than real ones, which drowns the actual transactions. Default
// the list to `completed` so the page opens on real activity.
const ONRAMP_STATUSES = [
  { value: 'completed', label: 'Completed' },
  { value: '', label: 'All statuses' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Expired' },
];
const OFFRAMP_STATUSES = [
  { value: 'completed', label: 'Completed' },
  { value: '', label: 'All statuses' },
  { value: 'awaiting_deposit', label: 'Awaiting deposit' },
  { value: 'funded', label: 'Funded' },
  { value: 'paying_out', label: 'Paying out' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Expired' },
];

const BASESCAN_TX = 'https://basescan.org/tx/';
const BASESCAN_ADDR = 'https://basescan.org/address/';

function txDisplay(hash: string): string {
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}
function addrDisplay(addr: string): string {
  return `${addr.slice(0, 8)}…${addr.slice(-4)}`;
}

/**
 * Column filter bar shared by both order screens. Every text column is covered
 * by the search box (matches ref, destination, recipient and id server-side);
 * the Created column by the date range; the NGN column by the amount range;
 * status by the dropdown. Editing any field resets to page 1.
 */
interface Filters extends OrderFilters {
  status: string;
  q: string;
  from: string;
  to: string;
  min_ngn: string;
  max_ngn: string;
}
const EMPTY: Filters = { status: 'completed', q: '', from: '', to: '', min_ngn: '', max_ngn: '' };

function FilterBar({
  filters,
  statuses,
  onChange,
  onReset,
}: {
  filters: Filters;
  statuses: { value: string; label: string }[];
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
}) {
  const active =
    filters.q || filters.from || filters.to || filters.min_ngn || filters.max_ngn || filters.status;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        marginBottom: 12,
      }}
    >
      <input
        placeholder="Search ref, address, account…"
        value={filters.q}
        onChange={(e) => onChange({ q: e.target.value })}
        style={{ height: 30, minWidth: 220, flex: '1 1 220px' }}
      />
      <select
        aria-label="Status"
        value={filters.status}
        onChange={(e) => onChange({ status: e.target.value })}
        style={{ height: 30 }}
      >
        {statuses.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <label className="muted" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
        From
        <input
          type="date"
          value={filters.from}
          onChange={(e) => onChange({ from: e.target.value })}
          style={{ height: 30 }}
        />
      </label>
      <label className="muted" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
        To
        <input
          type="date"
          value={filters.to}
          onChange={(e) => onChange({ to: e.target.value })}
          style={{ height: 30 }}
        />
      </label>
      <input
        type="number"
        placeholder="Min ₦"
        value={filters.min_ngn}
        onChange={(e) => onChange({ min_ngn: e.target.value })}
        style={{ height: 30, width: 90 }}
      />
      <input
        type="number"
        placeholder="Max ₦"
        value={filters.max_ngn}
        onChange={(e) => onChange({ max_ngn: e.target.value })}
        style={{ height: 30, width: 90 }}
      />
      {active && (
        <button className="btn btn-sm" onClick={onReset}>
          Clear
        </button>
      )}
    </div>
  );
}

function Pager({
  page,
  totalPages,
  hasMore,
  onPage,
}: {
  page: number;
  totalPages: number;
  hasMore: boolean;
  onPage: (p: number) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button className="btn btn-sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
        Previous
      </button>
      <span className="muted">
        {page} / {totalPages}
      </span>
      <button className="btn btn-sm" onClick={() => onPage(page + 1)} disabled={!hasMore}>
        Next
      </button>
    </div>
  );
}

// Convert the UI date (yyyy-mm-dd) to an inclusive ISO range end-of-day for `to`.
function toApiFilters(f: Filters): OrderFilters {
  return {
    status: f.status || undefined,
    q: f.q.trim() || undefined,
    from: f.from ? new Date(f.from).toISOString() : undefined,
    to: f.to ? new Date(f.to + 'T23:59:59').toISOString() : undefined,
    min_ngn: f.min_ngn || undefined,
    max_ngn: f.max_ngn || undefined,
  };
}

export function Offramps() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const orders = useResource(() => offramps.list(page, toApiFilters(filters)), [page, filters], {
    refreshMs: 20000,
  });
  const pagination = orders.data?.pagination;

  const patch = (p: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...p }));
    setPage(1);
  };

  return (
    <>
      <div className="page-header">
        <h1>Offramps</h1>
        <p>Crypto in → NGN to a bank, at your locked rate.</p>
      </div>

      {orders.error && <Alert>{orders.error}</Alert>}

      <FilterBar
        filters={filters}
        statuses={OFFRAMP_STATUSES}
        onChange={patch}
        onReset={() => {
          setFilters(EMPTY);
          setPage(1);
        }}
      />

      <Card
        title={pagination ? `${pagination.total} orders` : 'Orders'}
        actions={
          <Pager
            page={page}
            totalPages={pagination?.total_pages ?? 1}
            hasMore={!!pagination?.has_more}
            onPage={setPage}
          />
        }
      >
        {orders.loading ? (
          <Spinner />
        ) : !orders.data?.items.length ? (
          <Empty>No offramp orders match these filters.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Ref</th>
                  <th>Rate</th>
                  <th>USDC in</th>
                  <th>NGN out</th>
                  <th>Recipient</th>
                  <th>Deposit tx</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => (
                  <tr key={order.id}>
                    <td className="muted">{formatDateTime(order.created_at)}</td>
                    <td>
                      <Copyable value={order.integrator_ref ?? order.id} display={order.id.slice(0, 10)} />
                    </td>
                    <td className="mono">₦{order.rate}</td>
                    <td className="mono">{order.amount_usdc ?? '—'}</td>
                    <td className="mono">{order.amount_ngn ? `₦${order.amount_ngn}` : '—'}</td>
                    <td>
                      {order.recipient_name ?? (
                        <Copyable value={order.destination_account} display={order.destination_account} />
                      )}
                    </td>
                    <td>
                      <Copyable
                        value={order.settlement_tx_hash}
                        display={order.settlement_tx_hash ? txDisplay(order.settlement_tx_hash) : undefined}
                        href={order.settlement_tx_hash ? BASESCAN_TX + order.settlement_tx_hash : undefined}
                      />
                    </td>
                    <td>
                      <Badge tone={orderTone(order.status)}>{order.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

export function Onramps() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const orders = useResource(() => onramps.list(page, toApiFilters(filters)), [page, filters], {
    refreshMs: 20000,
  });
  const pagination = orders.data?.pagination;

  const patch = (p: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...p }));
    setPage(1);
  };

  return (
    <>
      <div className="page-header">
        <h1>Onramps</h1>
        <p>
          Each order's account number is a permanent channel: first payment at the locked rate,
          later payments requoted at your current rate.
        </p>
      </div>

      {orders.error && <Alert>{orders.error}</Alert>}

      <FilterBar
        filters={filters}
        statuses={ONRAMP_STATUSES}
        onChange={patch}
        onReset={() => {
          setFilters(EMPTY);
          setPage(1);
        }}
      />

      <Card
        title={pagination ? `${pagination.total} orders` : 'Orders'}
        actions={
          <Pager
            page={page}
            totalPages={pagination?.total_pages ?? 1}
            hasMore={!!pagination?.has_more}
            onPage={setPage}
          />
        }
      >
        {orders.loading ? (
          <Spinner />
        ) : !orders.data?.items.length ? (
          <Empty>No onramp orders match these filters.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Ref</th>
                  <th>Rate</th>
                  <th>Pay-in account</th>
                  <th>NGN in</th>
                  <th>USDC out</th>
                  <th>Destination</th>
                  <th>Delivery tx</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => (
                  <tr key={order.id}>
                    <td className="muted">{formatDateTime(order.created_at)}</td>
                    <td>
                      <Copyable value={order.integrator_ref ?? order.id} display={order.id.slice(0, 10)} />
                    </td>
                    <td className="mono">₦{order.rate}</td>
                    <td>
                      <Copyable value={order.payment_account?.account_number ?? null} />
                    </td>
                    <td className="mono">{order.amount_ngn ? `₦${order.amount_ngn}` : '—'}</td>
                    <td className="mono">{order.amount_usdc ?? '—'}</td>
                    <td>
                      <Copyable
                        value={order.destination_address}
                        display={addrDisplay(order.destination_address)}
                        href={BASESCAN_ADDR + order.destination_address}
                      />
                    </td>
                    <td>
                      <Copyable
                        value={order.delivery_tx_hash}
                        display={order.delivery_tx_hash ? txDisplay(order.delivery_tx_hash) : undefined}
                        href={order.delivery_tx_hash ? BASESCAN_TX + order.delivery_tx_hash : undefined}
                      />
                    </td>
                    <td>
                      <Badge tone={orderTone(order.status)}>{order.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
